import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/types";
import { z } from "zod";

export type MarketplaceAccessJSON = z.infer<typeof MarketplaceAccessSchema.MarketplaceAccessJSONZ>;

export type MarketplaceAccessParams = z.infer<typeof MarketplaceAccessSchema.MarketplaceAccessZ>;

export default class MarketplaceAccessSchema {
  public static readonly MarketplaceAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.MarketplaceAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    scope: z.enum(Scope).nullish(),
  });

  public static readonly MarketplaceAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.MarketplaceAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    scope: z.enum(Scope).optional(),
  });

  public static validate(values: unknown): MarketplaceAccessParams {
    return parseOrThrow(MarketplaceAccessSchema.MarketplaceAccessZ, values, {
      logError: true,
      schemaName: "MarketplaceAccess",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): MarketplaceAccessJSON {
    return parseOrThrow(MarketplaceAccessSchema.MarketplaceAccessJSONZ, values, {
      logError: true,
      schemaName: "MarketplaceAccess",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is MarketplaceAccessParams {
    try {
      MarketplaceAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is MarketplaceAccessJSON {
    try {
      MarketplaceAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: MarketplaceAccessJSON): MarketplaceAccessParams {
    const parsed = parseOrThrow(MarketplaceAccessSchema.MarketplaceAccessJSONZ, data, {
      logError: true,
      schemaName: "MarketplaceAccess",
      operation: "deserialize",
      payloadSample: data,
    });
    return MarketplaceAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: MarketplaceAccessParams): MarketplaceAccessJSON {
    const parsed = MarketplaceAccessSchema.validate(notification);
    return MarketplaceAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    MarketplaceAccessSchema.MarketplaceAccessJSONZ,
    MarketplaceAccessSchema.MarketplaceAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.MarketplaceAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        scope: data.scope ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.MarketplaceAccess as const,
        accessLevel: resource.accessLevel,
        scope: resource.scope,
      }),
    }
  );
}
